import { PrecisionUi } from "@blockdaemon-internal/precision-ui";
import { Center, Loader } from "@mantine/core";
import { QueryClientProvider } from "@tanstack/react-query";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { StrictMode, Suspense } from "react";
import { createRoot } from "react-dom/client";
import packageJson from "../package.json";
import { WidgetConfigProvider } from "./features/customization/hooks/use-widget-config";
import { WalletProvider } from "./features/wallet/components/wallet-provider";
import "./lib/i18n/init"; // Initialize i18n early
import { LocaleConfigWrapper } from "./lib/i18n/locale-config-wrapper";
import { routeTree } from "./routeTree.gen";
import { initializeSentry } from "./sentry-config";
import { AnalyticsProvider } from "./shared/components/analytics-provider";
import { GlobalErrorBoundary } from "./shared/components/error-boundary";
import { GlobalErrorListener } from "./shared/components/error-simulation-components";
import { NotFoundPage } from "./shared/components/not-found-page";
import { queryClient } from "./shared/lib/query-client";
import { initializeIframeTelemetry } from "./shared/utils/iframe-telemetry";
import "./styles/global.css";
import type { DefiTabRouteId } from "./features/defi/hooks/use-defi-tabs";

// Make version available globally
if (typeof window !== "undefined") {
  window.version = packageJson.version;
}

// Also make it available as a global variable (not just on window)
globalThis.version = packageJson.version;

// Polyfill for assert function used by bitcore-lib
if (typeof window !== "undefined") {
  // @ts-expect-error
  window.assert = (condition: unknown, message?: string) => {
    if (!condition) {
      throw new Error(message || "Assertion failed");
    }
  };
}

// Initialize services as early as possible
initializeSentry();
initializeIframeTelemetry();

// Create a new router instance
const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  defaultStaleTime: 5000,
  defaultNotFoundComponent: () => <NotFoundPage />,
});

// Register the router instance for type safety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }

  interface HistoryState {
    backTo: DefiTabRouteId;
  }
}

async function startApp() {
  const rootElement = document.getElementById("root");

  if (!rootElement) {
    throw new Error("Root element not found");
  }

  // Get color scheme override from URL parameters for customer embedding
  const { getColorSchemeOverride } = await import("./shared/utils/embed-detection");
  const colorSchemeOverride = getColorSchemeOverride();

  // Create root and render immediately
  const root = createRoot(rootElement);

  root.render(
    <StrictMode>
      <PrecisionUi forceColorScheme={colorSchemeOverride || undefined}>
        <GlobalErrorBoundary>
          <GlobalErrorListener />
          <Suspense
            fallback={
              <Center style={{ height: "100vh" }}>
                <Loader size="lg" />
              </Center>
            }
          >
            <QueryClientProvider client={queryClient}>
              <WidgetConfigProvider>
                <LocaleConfigWrapper>
                  <WalletProvider>
                    <AnalyticsProvider>
                      <RouterProvider router={router} />
                    </AnalyticsProvider>
                  </WalletProvider>
                </LocaleConfigWrapper>
              </WidgetConfigProvider>
            </QueryClientProvider>
          </Suspense>
        </GlobalErrorBoundary>
      </PrecisionUi>
    </StrictMode>,
  );
}

startApp();
